/**
 * @fileoverview Conflict-check brief view (MOR-993)
 *
 * Purpose: render a conflict check's description as the labelled card the
 * rest of the platform uses, instead of the "giant plain box" of pre-wrap
 * text it used to be. The description stays stored as the editable text the
 * practitioner approved; `parseConflictBrief` (shared with the vote email)
 * recovers the "Label: value" structure at render time, and anything a
 * practitioner rewrote free-hand degrades gracefully to paragraphs.
 *
 * Fields lay out as hairline-separated rows with a fixed label column, so a
 * long value (fifty trading names) never drags the labels around — and the
 * value itself is shaped by `ConflictBriefFieldValue`.
 *
 * Used by:
 *  - src/components/pre-appointment/pipeline-job-panel.tsx (pipeline card)
 *  - src/routes/_app/_auth/dashboard/-ui.job-conflict-check-panel.tsx
 *  - src/routes/conflict-check.$token.tsx (public vote page)
 */

import { parseConflictBrief } from "@cvx/preAppointment/conflictBriefFormat";
import { cn } from "@/utils/misc";
import { ConflictBriefFieldValue } from "./conflict-brief-field-value";

interface ConflictBriefViewProps {
  description: string;
  className?: string;
}

/**
 * @component
 * @example
 * <ConflictBriefView description={check.description} />
 */
export function ConflictBriefView({
  description,
  className,
}: ConflictBriefViewProps) {
  const blocks = parseConflictBrief(description);

  return (
    <div className={cn("grid gap-2.5", className)}>
      {blocks.map((block, index) =>
        block.kind === "paragraph" ? (
          <p
            key={index}
            className="whitespace-pre-wrap break-words text-sm leading-relaxed"
          >
            {block.text}
          </p>
        ) : (
          <dl
            key={index}
            className="divide-y divide-border/60 overflow-hidden rounded-lg border bg-card"
          >
            {block.fields.map((field, fieldIndex) => (
              <div
                key={fieldIndex}
                className="grid grid-cols-[minmax(8rem,11rem)_minmax(0,1fr)] gap-x-3 px-3 py-1.5"
              >
                <dt className="text-xs leading-5 text-muted-foreground">
                  {field.label}
                </dt>
                <dd className="min-w-0 text-xs leading-5">
                  <ConflictBriefFieldValue value={field.value} />
                </dd>
              </div>
            ))}
          </dl>
        ),
      )}
    </div>
  );
}
